/**
 * Calcule la date de Pâques pour une année donnée (algorithme de Butcher).
 * @param year L'année pour laquelle calculer Pâques.
 * @returns La date de Pâques.
 */
function getEaster(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/**
 * Vérifie si une date donnée est un jour férié en France.
 * @param date La date à vérifier.
 * @returns true si la date est un jour férié, false sinon.
 */
export function isPublicHoliday(date: Date): boolean {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed
  const day = date.getDate();
  const dateString = `${month}-${day}`;

  // Jours fériés fixes
  const fixedHolidays = [
    '0-1',   // 1er janvier
    '4-1',   // 1er mai
    '4-8',   // 8 mai
    '6-14',  // 14 juillet
    '7-15',  // 15 août
    '10-1',  // 1er novembre
    '10-11', // 11 novembre
    '11-25', // 25 décembre
  ];
  if (fixedHolidays.includes(dateString)) {
    return true;
  }

  // Jours fériés mobiles (basés sur Pâques)
  const easter = getEaster(year);
  const easterTime = easter.getTime();
  
  // Lundi de Pâques (+1 jour)
  const easterMonday = new Date(easterTime + 1 * 24 * 60 * 60 * 1000);
  // Ascension (+39 jours)
  const ascension = new Date(easterTime + 39 * 24 * 60 * 60 * 1000);
  // Lundi de Pentecôte (+50 jours)
  const pentecostMonday = new Date(easterTime + 50 * 24 * 60 * 60 * 1000);

  const mobileHolidays = [easterMonday, ascension, pentecostMonday];
  
  return mobileHolidays.some(holiday => 
    date.getDate() === holiday.getDate() &&
    date.getMonth() === holiday.getMonth() &&
    date.getFullYear() === holiday.getFullYear()
  );
}

/**
 * Vérifie si une date est dans la période d'été de haute saison
 * (du 14 juillet au dernier dimanche d'août inclus).
 * @param date La date à vérifier
 * @returns true si la date est dans la période, false sinon.
 */
export function isPeakSummer(date: Date): boolean {
    const year = date.getFullYear();
    const july14 = new Date(year, 6, 14); // 6 = Juillet

    // Trouver le dernier jour d'août
    const lastDayOfAugust = new Date(year, 7, 31); // 7 = Août
    // Trouver le dernier dimanche d'août
    const lastSundayOfAugust = new Date(lastDayOfAugust);
    lastSundayOfAugust.setDate(lastDayOfAugust.getDate() - lastDayOfAugust.getDay());

    return date >= july14 && date <= lastSundayOfAugust;
} 